import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Avatar, List, Input, Button, Spin, message, Badge, Divider,
  Upload, Space, Popover, Typography, Tooltip
} from 'antd';
import { 
  PaperClipOutlined, SendOutlined, SmileOutlined,
  CheckOutlined, CheckCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import Pusher from 'pusher-js';
import { BACKENDURL, BACKENDURLFiles, getUserData } from '../../constants';
import './Chat.css';

const { TextArea } = Input;

const UserChats = () => {
    const [users, setUsers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [messageText, setMessageText] = useState('');
    const messagesEndRef = useRef(null);
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUser, setTypingUser] = useState(null);
    const typingTimeoutRef = useRef(null);
    const pusherRef = useRef(null);
    const channelRef = useRef(null);
    const [unreadCounts, setUnreadCounts] = useState({});
        const getCurrentUser = useCallback(() => {
        try {
            const userData = getUserData();
            if (!userData || !userData.user) {
                message.error('User session expired. Please login again.');
                return null;
            }
            return userData.user;
        } catch (e) {
            console.error("Error getting user data:", e);
            return null;
        }
    }, []);
  // Define beforeUpload function before it's used
    const beforeUpload = (file) => {
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('File must be smaller than 10MB!');
            return false;
        }
        setFileList([file]);
        return false;
    };

       // Define handleTyping before it's used
    const handleTyping = useCallback(() => {
        if (!channelRef.current || !selectedUser) return;
        
        const currentUser = getCurrentUser();
        if (!currentUser) return;
        
        channelRef.current.trigger('client-typing', {
            userId: currentUser.id,
            userName: currentUser.name
        });
    }, [selectedUser, getCurrentUser]);

        // Define handleKeyPress before it's used
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        } else {
            handleTyping();
        }
    };


    useEffect(() => {
        const pusher = new Pusher('101b707300ca439b512c', {
            cluster: 'mt1',
            forceTLS: true,
            encrypted: true
        });
        pusherRef.current = pusher;

        return () => {
            pusher.disconnect();
        };
    }, []);

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (!selectedUser || !pusherRef.current) return;

        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.id) return;

        if (channelRef.current) {
            channelRef.current.unbind_all();
            channelRef.current.unsubscribe();
        }

        const channelName = `private-chat.${[currentUser.id, selectedUser.id].sort().join('-')}`;
        const channel = pusherRef.current.subscribe(channelName);
        channelRef.current = channel;

        channel.bind('message-sent', (data) => {
            if (data && data.message) {
                setMessages(prev => {
                    const messageExists = prev.some(msg => msg.id === data.message.id);
                    return messageExists ? prev : [...prev, data.message];
                });
                
                // Mark as delivered if it's our message
                if (data.message.sender_id === currentUser.id) {
                    markMessagesAsDelivered([data.message.id]);
                }
            }
        });

        channel.bind('client-typing', (data) => {
            if (data.userId !== currentUser.id) {
                setTypingUser(data.userName);
                setIsTyping(true);
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => {
                    setIsTyping(false);
                    setTypingUser(null);
                }, 2000);
            }
        });

        return () => {
            channel.unbind_all();
        };
    }, [selectedUser, getCurrentUser]);

    useEffect(() => {
        const currentUser = getCurrentUser();
        if (!currentUser || !pusherRef.current) return;

        const userChannel = pusherRef.current.subscribe(`private-user.${currentUser.id}`);
        
        userChannel.bind('App\\Events\\MessagesRead', (data) => {
            setMessages(prev => prev.map(msg => 
                data.message_ids.includes(msg.id)
                    ? { ...msg, read_at: data.read_at }
                    : msg
            ));
        });

        return () => {
            userChannel.unbind_all();
        };
    }, [getCurrentUser]);

    useEffect(() => {
        if (selectedUser) {
            fetchMessages();
        }
    }, [selectedUser]);

    useEffect(() => {
        scrollToBottom();
        
        // Mark messages as read when they become visible
        const currentUser = getCurrentUser();
        if (!currentUser || !selectedUser) return;

        const unreadMessages = messages.filter(
            msg => msg.receiver_id === currentUser.id && 
                  msg.sender_id === selectedUser.id &&
                  !msg.read_at
        );

        if (unreadMessages.length > 0) {
            markMessagesAsRead(unreadMessages.map(m => m.id));
        }
    }, [messages]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get('/chat/users');
            setUsers(data || []);
            
            // Fetch unread counts for each user
            const currentUser = getCurrentUser();
            if (currentUser) {
                const unreadData = await axiosInstance.get('/chat/unread-counts');
                setUnreadCounts(unreadData.data || {});
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            message.error('Failed to load users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            const { data } = await axiosInstance.get(`/chat/messages/${selectedUser.id}`);
            setMessages(data || []);
        } catch (error) {
            console.error("Error fetching messages:", error);
            message.error('Failed to load messages. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const markMessagesAsDelivered = async (messageIds) => {
        try {
            await axiosInstance.post('/chat/messages/delivered', {
                message_ids: messageIds
            });
            
            setMessages(prev => prev.map(msg => 
                messageIds.includes(msg.id) && !msg.delivered_at
                    ? { ...msg, delivered_at: new Date().toISOString() }
                    : msg
            ));
        } catch (error) {
            console.error("Error marking as delivered:", error);
        }
    };

    const markMessagesAsRead = async (messageIds) => {
        try {
            await axiosInstance.post('/chat/messages/read', {
                message_ids: messageIds
            });
            
            setMessages(prev => prev.map(msg => 
                messageIds.includes(msg.id) && !msg.read_at
                    ? { ...msg, read_at: new Date().toISOString() }
                    : msg
            ));
            
            // Update unread counts
            setUnreadCounts(prev => {
                const newCounts = {...prev};
                if (selectedUser && newCounts[selectedUser.id]) {
                    newCounts[selectedUser.id] = Math.max(0, newCounts[selectedUser.id] - messageIds.length);
                }
                return newCounts;
            });
        } catch (error) {
            console.error("Error marking as read:", error);
        }
    };

    const handleSendMessage = async () => {
        if (!messageText.trim() && fileList.length === 0) return;

        const currentUser = getCurrentUser();
        if (!currentUser || !currentUser.id) {
            message.error('Cannot send message: User not authenticated');
            return;
        }

        if (!selectedUser || !selectedUser.id) {
            message.error('Cannot send message: No recipient selected');
            return;
        }

        const formData = new FormData();
        formData.append('receiver_id', selectedUser.id);
        if (messageText.trim()) {
            formData.append('message', messageText);
        }
        if (fileList.length > 0) {
            formData.append('attachment', fileList[0].originFileObj);
        }

        // Create optimistic message
        const tempMessage = {
            id: `temp-${Date.now()}`,
            sender_id: currentUser.id,
            receiver_id: selectedUser.id,
            message: messageText,
            attachment: fileList[0]?.name || null,
            created_at: new Date().toISOString(),
            sent_at: new Date().toISOString(),
            delivered_at: null,
            read_at: null,
            isTemp: true,
            sender: {
                id: currentUser.id,
                name: currentUser.name,
                profile_pic: currentUser.profile_pic
            }
        };

        try {
            setUploading(true);
            setMessages(prev => [...prev, tempMessage]);
            setMessageText('');
            setFileList([]);
            
            const response = await axiosInstance.post('/chat/send', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (response.data?.data) {
                setMessages(prev => [
                    ...prev.filter(m => m.id !== tempMessage.id),
                    response.data.data
                ]);
            }
        } catch (error) {
            setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
            console.error("Error sending message:", error);
            message.error('Failed to send message. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const renderMessageStatus = (msg) => {
        const currentUser = getCurrentUser();
        if (!currentUser || msg.sender_id !== currentUser.id) return null;

        if (msg.read_at) {
            return (
                <Tooltip title="Read" placement="left">
                    <CheckCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
            );
        } else if (msg.delivered_at) {
            return (
                <Tooltip title="Delivered" placement="left">
                    <CheckCircleOutlined style={{ color: '#aaa' }} />
                </Tooltip>
            );
        } else if (msg.sent_at) {
            return (
                <Tooltip title="Sent" placement="left">
                    <CheckOutlined />
                </Tooltip>
            );
        } else {
            return (
                <Tooltip title="Sending..." placement="left">
                    <ClockCircleOutlined />
                </Tooltip>
            );
        }
    };

    const renderMessageContent = (msg) => {
        if (!msg.message && !msg.attachment) return null;
        
        const isReceived = msg.sender_id !== getCurrentUser()?.id;
        return (
            <div className={`message-content ${isReceived ? 'received' : 'sent'}`}>
                {msg.message && <div className="message-text">{msg.message}</div>}
                {msg.attachment && (
                    <div className="message-attachment">
                        {msg.attachment.includes('image') ? (
                            <img 
                                src={`${BACKENDURLFiles}/${msg.attachment}`} 
                                alt="attachment" 
                                style={{ maxWidth: '200px', maxHeight: '200px' }}
                            />
                        ) : (
                            <a 
                                href={`${BACKENDURLFiles}/${msg.attachment}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                            >
                                <PaperClipOutlined /> View Attachment
                            </a>
                        )}
                    </div>
                )}
                <div className="message-time">
                    {moment(msg.created_at).format('h:mm A')}
                    {renderMessageStatus(msg)}
                </div>
            </div>
        );
    };

    return (
        <div className="chat-container">
            <div className="chat-sidebar">
                <div className="chat-header">
                    <h3>Chats</h3>
                </div>
                <Divider style={{ margin: '12px 0' }} />
                <Spin spinning={loading}>
                    <List
                        dataSource={users}
                        renderItem={(user) => (
                            <List.Item 
                                className={`user-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                                onClick={() => setSelectedUser(user)}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Badge count={unreadCounts[user.id] || 0}>
                                            <Avatar 
                                                src={user.profile_pic ? `${BACKENDURLFiles}/${user.profile_pic}` : null}
                                            >
                                                {user.name.charAt(0)}
                                            </Avatar>
                                        </Badge>
                                    }
                                    title={user.name}
                                    description={""}
                                />
                            </List.Item>
                        )}
                    />
                </Spin>
            </div>
            <div className="chat-main">
                {selectedUser ? (
                    <>
                        <div className="chat-header">
                            <div className="user-info">
                                <Avatar 
                                    size="large"
                                    src={selectedUser.profile_pic ? `${BACKENDURLFiles}/${selectedUser.profile_pic}` : null}
                                >
                                    {selectedUser.name.charAt(0)}
                                </Avatar>
                                <span style={{ marginLeft: 12 }}>{selectedUser.name}</span>
                            </div>
                        </div>
                        <Divider style={{ margin: '12px 0' }} />

                        <div className="messages-container">
                            <Spin spinning={loading}>
                                {messages.length > 0 ? (
                                    <>
                                        {messages.map((msg) => (
                                            <div 
                                                key={msg.id} 
                                                className={`message-wrapper ${msg.sender_id === selectedUser.id ? 'received' : 'sent'}`}
                                            >
                                                {renderMessageContent(msg)}
                                            </div>
                                        ))}
                                        {isTyping && (
                                            <div className="message-wrapper received">
                                                <div className="message-content received">
                                                    <div className="typing-indicator">
                                                        <span></span>
                                                        <span></span>
                                                        <span></span>
                                                    </div>
                                                    <div className="message-time">
                                                        {typingUser} is typing...
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </>
                                ) : (
                                    <div className="empty-messages">
                                        <p>No messages yet. Start the conversation!</p>
                                    </div>
                                )}
                            </Spin>
                        </div>

                        <div className="message-input-area">
                            <Space.Compact style={{ width: '100%' }}>
                                <Popover
                                    content={
                                        <Upload
                                            fileList={fileList}
                                            beforeUpload={beforeUpload}
                                            onRemove={() => setFileList([])}
                                            maxCount={1}
                                        >
                                            <Button icon={<PaperClipOutlined />}>Attach File</Button>
                                        </Upload>
                                    }
                                    trigger="click"
                                    placement="topLeft"
                                >
                                    <Button icon={<PaperClipOutlined />} />
                                </Popover>
                                <TextArea
                                    placeholder="Type a message..."
                                    autoSize={{ minRows: 1, maxRows: 4 }}
                                    value={messageText}
                                    onChange={(e) => {
                                        setMessageText(e.target.value);
                                        if (e.target.value.trim()) handleTyping();
                                    }}
                                    onKeyPress={handleKeyPress}
                                />
                                <Button 
                                    type="primary" 
                                    icon={<SendOutlined />} 
                                    onClick={handleSendMessage}
                                    loading={uploading}
                                    disabled={!messageText.trim() && fileList.length === 0}
                                />
                            </Space.Compact>
                        </div>
                    </>
                ) : (
                    <div className="empty-chat">
                        <div className="empty-content">
                            <SmileOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                            <p>Select a user to start chatting</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserChats;